import { ContentPage } from '../../types/content';
import { markdownToContentBlocks } from './hldFundamentalsContent';

type ProblemSource = {
  id: string;
  file: string;
  readTime: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
};

const problems: ProblemSource[] = [
  {
    id: 'rate-limiter',
    file: 'problems/rate-limiter.md',
    readTime: '35–45 min read',
    difficulty: 'intermediate',
  },
  {
    id: 'consistent-hashing',
    file: 'problems/consistent-hashing.md',
    readTime: '20–30 min read',
    difficulty: 'intermediate',
  },
  {
    id: 'key-value-store',
    file: 'problems/key-value-store.md',
    readTime: '45–60 min read',
    difficulty: 'advanced',
  },
  {
    id: 'unique-id-generator',
    file: 'problems/unique-id-generator.md',
    readTime: '30–45 min read',
    difficulty: 'intermediate',
  },
  {
    id: 'url-shortener',
    file: 'problems/url-shortener.md',
    readTime: '35–50 min read',
    difficulty: 'intermediate',
  },
  {
    id: 'web-crawler',
    file: 'problems/web-crawler.md',
    readTime: '45–60 min read',
    difficulty: 'advanced',
  },
  {
    id: 'notification-system',
    file: 'problems/notification-system.md',
    readTime: '40–55 min read',
    difficulty: 'advanced',
  },
  {
    id: 'news-feed-system',
    file: 'problems/news-feed-system.md',
    readTime: '35–50 min read',
    difficulty: 'advanced',
  },
  {
    id: 'chat-system',
    file: 'problems/chat-system.md',
    readTime: '40–55 min read',
    difficulty: 'advanced',
  },
  {
    id: 'search-autocomplete',
    file: 'problems/search-autocomplete.md',
    readTime: '30–45 min read',
    difficulty: 'intermediate',
  },
  {
    id: 'youtube',
    file: 'problems/youtube.md',
    readTime: '40–55 min read',
    difficulty: 'advanced',
  },
  {
    id: 'google-drive',
    file: 'problems/google-drive.md',
    readTime: '40–55 min read',
    difficulty: 'advanced',
  },
];

/** Loads problem lessons authored as Markdown into the shared article renderer. */
export const loadHLDProblemContent = async (): Promise<Record<string, ContentPage>> => {
  const loaded = await Promise.all(problems.map(async (problem) => {
    const response = await fetch(`/hld-content/${problem.file}`);
    if (!response.ok) throw new Error(`Unable to load ${problem.file}`);

    const parsed = markdownToContentBlocks(await response.text());
    const slug = `hld-problem-${problem.id}`;

    return [slug, {
      id: slug,
      slug,
      title: parsed.title,
      description: parsed.blocks.find((block) => block.type === 'quote')?.content,
      blocks: parsed.blocks,
      category: 'system-design',
      subcategory: 'hld',
      tags: ['hld', 'hld-problem', problem.id],
      author: { name: 'Sachin', fullName: 'Sachin Saini' },
      lastUpdated: 'October 2026',
      readTime: problem.readTime,
      status: 'published',
      version: 1,
      metadata: { difficulty: problem.difficulty },
      viewCount: 0,
      likeCount: 0,
      averageRating: 0,
      ratingCount: 0,
      isActive: true,
    } satisfies ContentPage] as const;
  }));

  return Object.fromEntries(loaded);
};
